import React, { useState, useEffect } from 'react';
import type { Customer } from '../services/customersService';
import {
	Card,
	Table,
	Button,
	Input,
	Select,
	Space,
	Dropdown,
	Modal,
	Form,
	DatePicker,
	InputNumber,
	message,
	Tag,
	Tooltip,
	Row,
	Col,
	Typography,
} from 'antd';
import {
	PlusOutlined,
	SearchOutlined,
	EditOutlined,
	DeleteOutlined,
	ReloadOutlined,
	MoreOutlined,
	CalendarOutlined,
	StopOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import 'dayjs/locale/fr'; // Import French locale
import locale from 'antd/locale/fr_FR'; // Import Ant Design French locale
import { ConfigProvider } from 'antd';
import { contractsService, Contract, ContractFilters } from '../services/contractsService';
import { customersService } from '../services/customersService';
import { useAuth } from '../contexts/AuthContext';
import { exportToCSV } from '../utils/exportUtils';

// Configure dayjs to use French
dayjs.locale('fr');

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;


const ContractsPage: React.FC = () => {
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [customers, setCustomers] = useState<Customer[]>([]);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [filters, setFilters] = useState<ContractFilters>({});
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [editingContract, setEditingContract] = useState<Contract | null>(null);
	const [form] = Form.useForm();

	// États de statut et type
	const statusOptions = [
		{ value: 'draft', label: 'Brouillon', color: 'default' },
		{ value: 'active', label: 'Actif', color: 'green' },
		{ value: 'expired', label: 'Expiré', color: 'red' },
		{ value: 'terminated', label: 'Terminé', color: 'orange' },
		{ value: 'pending', label: 'En attente', color: 'blue' },
	];

	const typeOptions = [
		{ value: 'maintenance', label: 'Maintenance' },
		{ value: 'support', label: 'Support' },
		{ value: 'warranty', label: 'Garantie' },
		{ value: 'service', label: 'Service' },
	];

	const paymentFrequencyOptions = [
		{ value: 'monthly', label: 'Mensuel' },
		{ value: 'quarterly', label: 'Trimestriel' },
		{ value: 'yearly', label: 'Annuel' },
		{ value: 'one_time', label: 'Paiement unique' },
	];

	// Charger les contrats
	const loadContracts = async () => {
		setLoading(true);
		try {
			const response = await contractsService.getContracts({
				...filters,
				page: pagination.current,
				limit: pagination.pageSize,
			});
      
			setContracts(response.data.contracts);
			setPagination(prev => ({
				...prev,
				total: response.data.total,
			}));
		} catch (error) {
			message.error('Erreur lors du chargement des contrats');
			console.error('Erreur:', error);
		} finally {
			setLoading(false);
		}
	};

	// Charger les clients
	const loadCustomers = async () => {
		try {
			const response = await customersService.getCustomers();
			setCustomers(response.data.customers);
		} catch (error) {
			console.error('Erreur lors du chargement des clients:', error);
		}
	};

		useEffect(() => {
			const timer = setTimeout(() => {
				loadContracts();
			}, 300); // Délai pour éviter trop d'appels API lors de la saisie
    
			return () => clearTimeout(timer);
		}, [pagination.current, pagination.pageSize, filters]);

	useEffect(() => {
		loadCustomers();
	}, []);

	// Gérer les filtres
	const handleFiltersChange = (key: string, value: any) => {
		setFilters(prev => ({
			...prev,
			[key]: value,
		}));
		setPagination(prev => ({
			...prev,
			current: 1,
		}));
	};

	// Ouvrir le modal de création/édition
	const openModal = (contract?: Contract) => {
		setEditingContract(contract || null);
		setIsModalVisible(true);
    
		if (contract) {
			form.setFieldsValue({
				...contract,
				start_date: dayjs(contract.start_date),
				end_date: dayjs(contract.end_date),
			});
		} else {
			form.resetFields();
		}
	};

	// Fermer le modal
	const closeModal = () => {
		setIsModalVisible(false);
		setEditingContract(null);
		form.resetFields();
	};

	// Sauvegarder le contrat
	const handleSave = async (values: any) => {
		try {
			const contractData = {
				...values,
				start_date: values.start_date.format('YYYY-MM-DD'),
				end_date: values.end_date.format('YYYY-MM-DD'),
			};

			if (editingContract) {
				await contractsService.updateContract(editingContract.id, contractData);
				message.success('Contrat mis à jour avec succès');
			} else {
				await contractsService.createContract(contractData);
				message.success('Contrat créé avec succès');
			}

			closeModal();
			await loadCustomers(); // Recharge la liste des clients après création/modification
			loadContracts();
		} catch (error) {
			message.error('Erreur lors de la sauvegarde');
			console.error('Erreur:', error);
		}
	};

	// Supprimer un contrat
	const handleDelete = async (id: number) => {
		try {
			await contractsService.deleteContract(id);
			message.success('Contrat supprimé avec succès');
			loadContracts();
		} catch (error) {
			message.error('Erreur lors de la suppression');
			console.error('Erreur:', error);
		}
	};

	// Renouveler un contrat

	const handleRenew = (contract: Contract) => {
		let newEndDate = '';
		let newAmount: number | undefined = undefined;
		let dateValue: any = null;
		Modal.confirm({
			title: 'Renouveler le contrat',
			content: (
				<div>
					<p>Voulez-vous renouveler le contrat "{contract.title}" ?</p>
					<DatePicker
						placeholder="Nouvelle date de fin"
						style={{ width: '100%', marginBottom: 8 }}
						onChange={(date) => {
							dateValue = date;
							newEndDate = date ? date.format('YYYY-MM-DD') : '';
						}}
					/>
					<InputNumber
						style={{ width: '100%' }}
						min={0}
						precision={2}
						placeholder="Nouveau montant (optionnel)"
						onChange={(value) => {
							newAmount = value as number;
						}}
					/>
				</div>
			),
			okText: 'Renouveler',
			cancelText: 'Annuler',
			onOk: async () => {
				if (!newEndDate) {
					message.error('Veuillez sélectionner une nouvelle date de fin');
					return Promise.reject();
				}
				try {
					await contractsService.renewContract(contract.id, newEndDate, newAmount);
					message.success('Contrat renouvelé avec succès');
					loadContracts();
				} catch (error) {
					message.error('Erreur lors du renouvellement');
					console.error('Erreur:', error);
				}
			},
		});
	};

	const handleCancel = (id: number) => {
		Modal.confirm({
			title: 'Annuler le contrat',
			content: (
				<Input.TextArea
					placeholder="Raison de l'annulation"
					autoSize={{ minRows: 2, maxRows: 4 }}
					onChange={(e) => {
						(Modal as any).reason = e.target.value;
					}}
				/>
			),
			okText: 'Annuler le contrat',
			cancelText: 'Fermer',
			onOk: async () => {
				const reason = (Modal as any).reason || 'Annulation via dashboard';
				try {
					await contractsService.cancelContract(id, reason);
					message.success('Contrat annulé avec succès');
					loadContracts();
				} catch (error) {
					message.error('Erreur lors de l\'annulation');
					console.error('Erreur:', error);
				}
			},
		});
	};

	// Obtenir la couleur du statut
	const getStatusColor = (status: string) => {
		const statusOption = statusOptions.find(option => option.value === status);
		return statusOption?.color || 'default';
	};

	// Obtenir le label du statut
	const getStatusLabel = (status: string) => {
		const statusOption = statusOptions.find(option => option.value === status);
		return statusOption?.label || status;
	};

	// Obtenir le label du type
	const getTypeLabel = (type: string) => {
		const typeOption = typeOptions.find(option => option.value === type);
		return typeOption?.label || type;
	};

	// Obtenir le label de la fréquence de paiement
	const getPaymentFrequencyLabel = (frequency: string) => {
		const frequencyOption = paymentFrequencyOptions.find(option => option.value === frequency);
		return frequencyOption?.label || frequency;
	};

	// Colonnes du tableau
	const columns: ColumnsType<Contract> = [
		{
			title: 'Référence',
			dataIndex: 'reference',
			key: 'reference',
			width: 150,
			render: (text: string) => <span style={{ fontWeight: 'bold', color: '#1890ff' }}>{text}</span>,
		},
		{
			title: 'Client',
			key: 'customer',
			width: 150,
			render: (_, record) => 
				record.customer ? `${record.customer.first_name} ${record.customer.last_name}` : 'N/A',
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			width: 120,
			render: (type: string) => getTypeLabel(type),
		},
		{
			title: 'Statut',
			dataIndex: 'status',
			key: 'status',
			width: 120,
			render: (status: string) => (
				<Tag color={getStatusColor(status)}>
					{getStatusLabel(status)}
				</Tag>
			),
		},
		{
			title: 'Période',
			key: 'period',
			width: 200,
			render: (_, record) => (
				<div>
					<div>{dayjs(record.start_date).format('DD/MM/YYYY')}</div>
					<div style={{ fontSize: '12px', color: '#999' }}>
						au {dayjs(record.end_date).format('DD/MM/YYYY')}
					</div>
				</div>
			),
		},
			{
				title: 'Montant',
				dataIndex: 'amount',
				key: 'amount',
				width: 120,
				render: (amount: number) => `${amount.toLocaleString()} FCFA`,
			},
		{
			title: 'Fréquence',
			dataIndex: 'payment_frequency',
			key: 'payment_frequency',
			width: 120,
			render: (frequency: string) => getPaymentFrequencyLabel(frequency),
		},
		{
			title: 'Actions',
			key: 'actions',
			width: 120,
			fixed: 'right',
			render: (_, record) => (
				<Space>
					<Tooltip title="Modifier">
						<Button
							type="text"
							icon={<EditOutlined />}
							onClick={() => openModal(record)}
						/>
					</Tooltip>
					<Dropdown
						menu={{
							items: [
								{
									key: 'renew',
									label: 'Renouveler',
									icon: <CalendarOutlined />,
									disabled: record.status !== 'active',
									onClick: () => handleRenew(record),
								},
								{
									key: 'cancel',
									label: 'Annuler',
									icon: <StopOutlined />,
									disabled: !['active', 'pending'].includes(record.status),
									onClick: () => handleCancel(record.id),
								},
								{
									type: 'divider',
								},
								{
									key: 'delete',
									label: 'Supprimer',
									icon: <DeleteOutlined />,
									danger: true,
									onClick: () => {
										Modal.confirm({
											title: 'Confirmer la suppression',
											content: 'Êtes-vous sûr de vouloir supprimer ce contrat ?',
											onOk: () => handleDelete(record.id),
										});
									},
								},
							],
						}}
					>
						<Button type="text" icon={<MoreOutlined />} />
					</Dropdown>
				</Space>
			),
		},
	];

	const { currentUser } = useAuth();
	const isAgent = currentUser?.role === 'agent';
	const [exporting, setExporting] = useState(false);

	const handleExport = () => {
		try {
			setExporting(true);
			const rows = contracts.map(c => {
				const custName = c.customer ? `${c.customer.first_name || ''} ${c.customer.last_name || ''}`.trim() : 'N/A';
				return {
					reference: c.reference || `#${c.id}`,
					client: custName,
					type: getTypeLabel(c.type),
					statut: getStatusLabel(c.status),
					date_debut: c.start_date ? dayjs(c.start_date).format('DD/MM/YYYY') : '',
					date_fin: c.end_date ? dayjs(c.end_date).format('DD/MM/YYYY') : '',
					montant: isAgent ? '' : (c.amount ? `${c.amount.toLocaleString()} FCFA` : '0 FCFA'),
					frequence: getPaymentFrequencyLabel(c.payment_frequency),
				};
			});

			const headers = [
				{ header: 'Référence', key: 'reference' },
				{ header: 'Client', key: 'client' },
				{ header: 'Type', key: 'type' },
				{ header: 'Statut', key: 'statut' },
				{ header: 'Date de Début', key: 'date_debut' },
				{ header: 'Date de Fin', key: 'date_fin' },
				...(!isAgent ? [{ header: 'Montant', key: 'montant' }] : []),
				{ header: 'Fréquence Paiement', key: 'frequence' },
			];

			exportToCSV(rows, headers, `contrats_${new Date().toISOString().slice(0, 10)}.csv`);
			message.success('Export des contrats généré avec succès');
		} catch (err) {
			console.error('Erreur export contrats:', err);
			message.error("Erreur lors de l'export des contrats");
		} finally {
			setExporting(false);
		}
	};

	return (
		<ConfigProvider locale={locale}>
			<div>
				<div style={{ marginBottom: 16 }}>
					<Title level={2}>Gestion des Contrats</Title>
				</div>

				<Card>
				{/* Filtres et actions */}
				<Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
					<Col span={6}>
						<Input
							placeholder="Rechercher par référence ou titre..."
							prefix={<SearchOutlined />}
							onChange={(e) => handleFiltersChange('search', e.target.value)}
							allowClear
						/>
					</Col>
					<Col span={4}>
						<Select
							placeholder="Statut"
							onChange={(value) => handleFiltersChange('status', value)}
							allowClear
							style={{ width: '100%' }}
						>
							{statusOptions.map(option => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
					</Col>
					<Col span={4}>
						<Select
							placeholder="Type"
							onChange={(value) => handleFiltersChange('type', value)}
							allowClear
							style={{ width: '100%' }}
						>
							{typeOptions.map(option => (
								<Option key={option.value} value={option.value}>
									{option.label}
								</Option>
							))}
						</Select>
					</Col>
					<Col span={4}>
						<Select
							placeholder="Client"
							onChange={(value) => handleFiltersChange('customer_id', value)}
							allowClear
							style={{ width: '100%' }}
							showSearch
											filterOption={(input, option) => {
												const label = typeof option?.children === 'string' ? option.children : '';
												return label.toLowerCase().includes(input.toLowerCase());
											}}
						>
															{customers.map(customer => {
																const userId = customer.user?.id || customer.user_id;
																if (!userId) return null;
																
																return (
																	<Option key={userId} value={userId}>
																		{customer.first_name} {customer.last_name}
																	</Option>
																);
															})}
						</Select>
					</Col>
					<Col span={6}>
						<Space wrap>
							<Button
								icon={<DownloadOutlined />}
								onClick={handleExport}
								loading={exporting}
							>
								Exporter
							</Button>
							<Button
								type="primary"
								icon={<PlusOutlined />}
								onClick={() => openModal()}
							>
								Nouveau Contrat
							</Button>
							<Button
								icon={<ReloadOutlined />}
								onClick={loadContracts}
							>
								Actualiser
							</Button>
						</Space>
					</Col>
				</Row>

				{/* Tableau */}
				<Table
					columns={columns}
					dataSource={contracts}
					rowKey="id"
					loading={loading}
					pagination={{
						current: pagination.current,
						pageSize: pagination.pageSize,
						total: pagination.total,
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) =>
							`${range[0]}-${range[1]} sur ${total} contrats`,
						onChange: (page, pageSize) => {
							setPagination(prev => ({
								...prev,
								current: page,
								pageSize: pageSize || 10,
							}));
						},
					}}
					scroll={{ x: 1200 }}
				/>
			</Card>

			{/* Modal de création/édition */}
			<Modal
				title={editingContract ? 'Modifier le Contrat' : 'Nouveau Contrat'}
				open={isModalVisible}
				onCancel={closeModal}
				width={800}
				footer={null}
			>
				<Form
					form={form}
					layout="vertical"
					onFinish={handleSave}
				>
					{editingContract && (
						<Form.Item
							name="reference"
							label="Référence"
						>
							<Input disabled style={{ fontWeight: 'bold', color: '#1890ff' }} />
						</Form.Item>
					)}

					<Row gutter={16}>
						<Col span={8}>
							<Form.Item
								name="customer_id"
								label="Client"
								rules={[{ required: true, message: 'Client requis' }]}
							>
								<Select
									showSearch
														filterOption={(input, option) => {
															const label = typeof option?.children === 'string' ? option.children : '';
															return label.toLowerCase().includes(input.toLowerCase());
														}}
								>
									{customers.map(customer => {
										// Utiliser customer.user.id si disponible, sinon customer.user_id
										const userId = customer.user?.id || customer.user_id;
										if (!userId) return null;
										
										return (
											<Option key={userId} value={userId}>
												{customer.first_name} {customer.last_name}
											</Option>
										);
									})}
								</Select>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="type"
								label="Type"
								rules={[{ required: true, message: 'Type requis' }]}
							>
								<Select>
									{typeOptions.map(option => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="status"
								label="Statut"
								rules={[{ required: true, message: 'Statut requis' }]}
							>
								<Select>
									{statusOptions.map(option => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={8}>
							<Form.Item
								name="start_date"
								label="Date de début"
								rules={[{ required: true, message: 'Date de début requise' }]}
							>
								<DatePicker style={{ width: '100%' }} />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="end_date"
								label="Date de fin"
								rules={[{ required: true, message: 'Date de fin requise' }]}
							>
								<DatePicker style={{ width: '100%' }} />
							</Form.Item>
						</Col>
						<Col span={8}>
							<Form.Item
								name="payment_frequency"
								label="Fréquence de paiement"
								rules={[{ required: true, message: 'Fréquence requise' }]}
							>
								<Select>
									{paymentFrequencyOptions.map(option => (
										<Option key={option.value} value={option.value}>
											{option.label}
										</Option>
									))}
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								name="amount"
								label="Montant (FCFA)"
								rules={[
									{ required: true, message: 'Montant requis' },
									{ type: 'number', min: 0, message: 'Montant invalide' }
								]}
							>
								<InputNumber
									style={{ width: '100%' }}
									min={0}
									precision={2}
									formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
														parser={(value) => {
															const cleaned = value?.replace(/\$\s?|(,*)/g, '') || '0';
															return parseFloat(cleaned) as any;
														}}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item
						name="description"
						label="Description"
					>
						<TextArea rows={3} />
					</Form.Item>

					<Form.Item
						name="terms_and_conditions"
						label="Termes et conditions"
					>
						<TextArea rows={4} />
					</Form.Item>

					<Form.Item
						name="notes"
						label="Notes"
					>
						<TextArea rows={3} />
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
						<Space>
							<Button onClick={closeModal}>
								Annuler
							</Button>
							<Button type="primary" htmlType="submit">
								{editingContract ? 'Mettre à jour' : 'Créer'}
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
			</div>
		</ConfigProvider>
	);
};

export default ContractsPage;